import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AreaShell from '../../chrome/AreaShell';
import { Checkbox, ConfirmDialog, Dialog, Field, SectionCard, TextInput, Toggle } from '../../components/forms';
import { TCard, TEmpty, TFoot, Toolbar, TSkeleton } from '../../components/table';
import { useToast } from '../../components/roster';
import useFeatureFlagManagement from '@/src/Features/components/hooks/useFeatureFlagManagement';
import './admin-content.css';

/*
 * Feature flags and their role associations. Flag switches apply at once
 * (optimistic, reverted on failure); Live and Public ask first because they
 * reach every account. The role matrix batches behind one Save.
 */

interface FlagRow {
    id: number;
    key: string;
    enabled: boolean;
    enabledForDevelopment: boolean;
    isPublic?: boolean;
}

interface RoleRow {
    name: string;
    roleId: number;
    keys: string[];
}

type FlagField = 'enabled' | 'enabledForDevelopment' | 'isPublic';

const FIELD_LABEL: Record<FlagField, string> = {
    enabled: 'Live',
    enabledForDevelopment: 'Dev',
    isPublic: 'Public',
};

const SCOPE: Record<FlagField, string> = {
    enabled: 'Applies in production for every account whose role has this flag, across every org.',
    enabledForDevelopment: 'Applies in development environments only.',
    isPublic: 'Exposes the flag to unauthenticated visitors (login and public gallery pages) as well as signed-in users.',
};

const EMPTY_DRAFT = { key: '', enabled: false, enabledForDevelopment: true, isPublic: false };

const byKey = (a: FlagRow, b: FlagRow) => a.key.localeCompare(b.key);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export default function AdminFeaturesV3() {
    const {
        fetchFeatureFlags,
        createFeatureFlag,
        updateFeatureFlag,
        deleteFeatureFlag,
        getFeatureFlagsForRoles,
        addFeatureFlagToRole,
        removeFeatureFlagFromRole,
    } = useFeatureFlagManagement();
    const toast = useToast();

    const [flags, setFlags] = useState<FlagRow[] | null>(null);
    const [flagsFailed, setFlagsFailed] = useState(false);
    const [roles, setRoles] = useState<RoleRow[] | null>(null);
    const [rolesFailed, setRolesFailed] = useState(false);
    const [query, setQuery] = useState('');

    const loadFlags = useCallback(async () => {
        try {
            const rows: FlagRow[] = (await fetchFeatureFlags()) ?? [];
            setFlags([...rows].sort(byKey));
            setFlagsFailed(false);
        } catch {
            setFlagsFailed(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* the hook already returns an array of { name, keys, roleId } */
    const loadRoles = useCallback(async () => {
        try {
            const list = [...(((await getFeatureFlagsForRoles()) as RoleRow[]) ?? [])].sort((a, b) =>
                a.name.localeCompare(b.name),
            );
            setRoles(list);
            setRolesFailed(false);
            return list;
        } catch {
            setRolesFailed(true);
            return null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadFlags();
        loadRoles();
    }, [loadFlags, loadRoles]);

    /* ---- flag switches ---- */
    const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
    const [confirmToggle, setConfirmToggle] = useState<{ row: FlagRow; field: FlagField; value: boolean } | null>(null);

    const applyToggle = async (row: FlagRow, field: FlagField, value: boolean) => {
        if (pendingIds.has(row.id)) return;
        const next = { ...row, [field]: value };
        setPendingIds((s) => new Set(s).add(row.id));
        setFlags((cur) => cur && cur.map((f) => (f.id === row.id ? next : f)));
        const ok = await updateFeatureFlag(next)
            .then(() => true)
            .catch(() => false);
        setPendingIds((s) => {
            const n = new Set(s);
            n.delete(row.id);
            return n;
        });
        if (!ok) {
            setFlags((cur) => cur && cur.map((f) => (f.id === row.id ? row : f)));
            toast.show(`Couldn’t update ${row.key}. Nothing changed.`);
        } else {
            toast.show(`${row.key}: ${FIELD_LABEL[field]} ${value ? 'on' : 'off'}.`);
        }
    };

    const requestToggle = (row: FlagRow, field: FlagField, value: boolean) => {
        if (field === 'enabledForDevelopment') applyToggle(row, field, value);
        else setConfirmToggle({ row, field, value });
    };

    /* ---- create ---- */
    const [createOpen, setCreateOpen] = useState(false);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [createError, setCreateError] = useState('');
    const [creating, setCreating] = useState(false);
    const draftKey = draft.key.trim().toUpperCase();
    const keyTaken = !!flags?.some((f) => f.key === draftKey);
    const keyShapeOk = /^[A-Z0-9_]+$/.test(draftKey);

    const openCreate = () => {
        setDraft(EMPTY_DRAFT);
        setCreateError('');
        setCreateOpen(true);
    };

    const submitCreate = async () => {
        if (creating || !draftKey || keyTaken) return;
        setCreating(true);
        setCreateError('');
        const ok = await createFeatureFlag({ ...draft, key: draftKey })
            .then(() => true)
            .catch(() => false);
        setCreating(false);
        if (ok) {
            setCreateOpen(false);
            toast.show(`Created ${draftKey}.`);
            loadFlags();
        } else {
            setCreateError('Couldn’t create the flag. Try again.');
        }
    };

    /* ---- delete ---- */
    const [deleting, setDeleting] = useState<FlagRow | null>(null);
    const removeFlag = async (row: FlagRow) => {
        if (pendingIds.has(row.id)) return;
        setPendingIds((s) => new Set(s).add(row.id));
        const ok = await deleteFeatureFlag(row.key)
            .then(() => true)
            .catch(() => false);
        setPendingIds((s) => {
            const n = new Set(s);
            n.delete(row.id);
            return n;
        });
        if (ok) {
            setFlags((cur) => cur && cur.filter((f) => f.id !== row.id));
            toast.show(`Deleted ${row.key}.`);
            loadRoles();
        } else {
            toast.show(`Couldn’t delete ${row.key}.`);
        }
    };

    /* ---- role matrix, batched ---- */
    const [roleOpen, setRoleOpen] = useState<RoleRow | null>(null);
    const [roleDraft, setRoleDraft] = useState<Set<string>>(new Set());
    const [roleError, setRoleError] = useState('');
    const [roleSaving, setRoleSaving] = useState(false);
    const [roleQuery, setRoleQuery] = useState('');
    const savingRef = useRef(false);

    const openRole = (role: RoleRow) => {
        setRoleDraft(new Set(role.keys));
        setRoleError('');
        setRoleQuery('');
        setRoleOpen(role);
    };

    const roleDiff = useMemo(() => {
        if (!roleOpen || !flags) return { additions: [] as FlagRow[], removals: [] as FlagRow[] };
        const before = new Set(roleOpen.keys);
        return {
            additions: flags.filter((f) => roleDraft.has(f.key) && !before.has(f.key)),
            removals: flags.filter((f) => !roleDraft.has(f.key) && before.has(f.key)),
        };
    }, [roleOpen, roleDraft, flags]);
    const changeCount = roleDiff.additions.length + roleDiff.removals.length;

    const submitRole = async () => {
        if (!roleOpen || savingRef.current) return;
        if (changeCount === 0) {
            setRoleOpen(null);
            return;
        }
        savingRef.current = true;
        setRoleSaving(true);
        setRoleError('');
        /* sequential, like v2: one request per change */
        let failed = 0;
        for (const flag of roleDiff.additions) {
            const ok = await addFeatureFlagToRole(roleOpen.roleId, flag.id)
                .then(() => true)
                .catch(() => false);
            if (!ok) failed++;
        }
        for (const flag of roleDiff.removals) {
            const ok = await removeFeatureFlagFromRole(roleOpen.roleId, flag.id)
                .then(() => true)
                .catch(() => false);
            if (!ok) failed++;
        }
        savingRef.current = false;
        setRoleSaving(false);
        const fresh = await loadRoles();
        if (failed === 0) {
            toast.show(`Saved ${plural(changeCount, 'change')} for ${roleOpen.name}.`);
            setRoleOpen(null);
        } else {
            const now = fresh?.find((r) => r.roleId === roleOpen.roleId) ?? { ...roleOpen, keys: [] };
            setRoleOpen(now);
            setRoleDraft(new Set(now.keys));
            setRoleError(`${plural(failed, 'change')} failed. The list now shows what actually saved.`);
        }
    };

    const q = query.trim().toLowerCase();
    const visible = (flags ?? []).filter((f) => !q || f.key.toLowerCase().includes(q));
    const rq = roleQuery.trim().toLowerCase();
    const roleVisible = (flags ?? []).filter((f) => !rq || f.key.toLowerCase().includes(rq));
    const rolesFor = (key: string) => (roles ?? []).filter((r) => r.keys.includes(key)).map((r) => r.name);

    return (
        <AreaShell
            area="admin"
            tab="features"
            sub="Feature flags change what every user can see and do, across every org."
            actions={
                <button type="button" className="tb-new" onClick={openCreate} disabled={flags === null}>
                    Add flag
                </button>
            }
        >
            <div className="stack">
                <div>
                    <Toolbar searchValue={query} onSearch={setQuery} searchPlaceholder="Search flag keys" filterCount={0} />
                    {flagsFailed ? (
                        <TCard>
                            <TEmpty message="Couldn’t load feature flags." actionLabel="Retry" onAction={loadFlags} />
                        </TCard>
                    ) : flags === null ? (
                        <TCard>
                            <TSkeleton rows={5} />
                        </TCard>
                    ) : visible.length === 0 ? (
                        <TCard>
                            <TEmpty
                                message={flags.length === 0 ? 'No feature flags yet.' : `No flag matches “${query}”.`}
                                actionLabel={flags.length === 0 ? 'Add flag' : 'Clear search'}
                                onAction={flags.length === 0 ? openCreate : () => setQuery('')}
                            />
                        </TCard>
                    ) : (
                        <SectionCard title="Flags" note="switches save immediately">
                            {visible.map((row) => {
                                const busy = pendingIds.has(row.id);
                                const roleNames = rolesFor(row.key);
                                return (
                                    <div className="adm-flag" key={row.id} aria-busy={busy}>
                                        <span className="k">
                                            {row.key}
                                            <br />
                                            <span className="sub2" style={{ fontFamily: 'var(--sans)', fontWeight: 400 }}>
                                                {roles === null
                                                    ? ' '
                                                    : roleNames.length === 0
                                                      ? 'No roles'
                                                      : `Roles: ${roleNames.join(', ')}`}
                                            </span>
                                        </span>
                                        <span className="sw">
                                            {(Object.keys(FIELD_LABEL) as FlagField[]).map((field) => (
                                                <Toggle
                                                    key={field}
                                                    label={FIELD_LABEL[field]}
                                                    aria-label={`${FIELD_LABEL[field]} for ${row.key}`}
                                                    title={SCOPE[field]}
                                                    checked={!!row[field]}
                                                    disabled={busy}
                                                    onChange={(e) => requestToggle(row, field, e.target.checked)}
                                                />
                                            ))}
                                            <button
                                                type="button"
                                                className="tb-btn danger-soft"
                                                disabled={busy}
                                                aria-label={`Delete ${row.key}`}
                                                onClick={() => setDeleting(row)}
                                            >
                                                Delete
                                            </button>
                                        </span>
                                    </div>
                                );
                            })}
                        </SectionCard>
                    )}
                    {flags !== null && !flagsFailed && (
                        <TFoot>
                            {q ? `${visible.length} of ${flags.length} · filtered` : plural(flags.length, 'flag')} · Live = production,
                            Dev = development, Public = also signed-out visitors
                        </TFoot>
                    )}
                </div>

                <SectionCard title="Role access" note="which flags each account role gets">
                    <p className="adm-scope">
                        A role’s flags apply to every account with that role, in every org. Changes batch behind Save.
                    </p>
                    {rolesFailed ? (
                        <>
                            <p className="quiet-empty" style={{ marginBottom: 10 }}>Couldn’t load role access.</p>
                            <button type="button" className="tb-btn" onClick={loadRoles}>
                                Retry
                            </button>
                        </>
                    ) : roles === null ? (
                        <p className="quiet-empty">Loading…</p>
                    ) : roles.length === 0 ? (
                        <p className="quiet-empty">No role has any flags yet.</p>
                    ) : (
                        roles.map((role) => (
                            <div className="trow2" key={role.roleId}>
                                <span style={{ minWidth: 0, flex: 1 }}>
                                    <span className="nm2">{role.name}</span>
                                    <br />
                                    <span className="sub2">{plural(role.keys.length, 'flag')}</span>
                                </span>
                                <button
                                    type="button"
                                    className="tb-btn"
                                    disabled={flags === null}
                                    aria-label={`Edit flags for ${role.name}`}
                                    onClick={() => openRole(role)}
                                >
                                    Edit
                                </button>
                            </div>
                        ))
                    )}
                </SectionCard>
            </div>

            <Dialog
                open={createOpen}
                title="Add feature flag"
                onClose={() => !creating && setCreateOpen(false)}
                onSubmit={submitCreate}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setCreateOpen(false)} disabled={creating}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={creating || !draftKey || keyTaken}>
                            {creating ? 'Creating…' : 'Create flag'}
                        </button>
                    </>
                }
            >
                <Field
                    label="Key"
                    help={
                        draftKey && !keyShapeOk
                            ? 'Keys are usually UPPER_SNAKE_CASE (letters, numbers, underscores).'
                            : 'Saved in upper case. Code checks for this exact key.'
                    }
                    error={keyTaken ? 'A flag with this key already exists.' : createError || undefined}
                >
                    <TextInput
                        autoFocus
                        className="input adm-mono"
                        placeholder="MY_FEATURE_V1"
                        value={draft.key}
                        style={{ textTransform: 'uppercase' }}
                        onChange={(e) => setDraft((d) => ({ ...d, key: e.target.value }))}
                    />
                </Field>
                <div style={{ marginTop: 14 }}>
                    {(Object.keys(FIELD_LABEL) as FlagField[]).map((field) => (
                        <div className="mrow-t" key={field}>
                            <span>
                                <span className="lbl">
                                    {field === 'enabled'
                                        ? 'Live (production)'
                                        : field === 'enabledForDevelopment'
                                          ? 'Development'
                                          : 'Public (signed-out visitors)'}
                                </span>
                                <br />
                                <span className="sub3">{SCOPE[field]}</span>
                            </span>
                            <span className="tg">
                                <Toggle
                                    label=""
                                    aria-label={FIELD_LABEL[field]}
                                    checked={draft[field]}
                                    onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.checked }))}
                                />
                            </span>
                        </div>
                    ))}
                </div>
            </Dialog>

            <Dialog
                open={roleOpen !== null}
                title={roleOpen ? `Flags for ${roleOpen.name}` : ''}
                onClose={() => !roleSaving && setRoleOpen(null)}
                onSubmit={submitRole}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setRoleOpen(null)} disabled={roleSaving}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={roleSaving || changeCount === 0}>
                            {roleSaving ? 'Saving…' : changeCount === 0 ? 'No changes' : `Save ${plural(changeCount, 'change')}`}
                        </button>
                    </>
                }
            >
                {roleOpen && (
                    <>
                        <p className="adm-scope">
                            Applies to every account with the <b>{roleOpen.name}</b> role, in every org.
                            {changeCount > 0 &&
                                ` Saving turns on ${plural(roleDiff.additions.length, 'flag')} and off ${plural(roleDiff.removals.length, 'flag')} for all of them.`}
                        </p>
                        <TextInput
                            aria-label="Filter flags"
                            placeholder="Filter flags"
                            value={roleQuery}
                            onChange={(e) => setRoleQuery(e.target.value)}
                            style={{ marginBottom: 8 }}
                        />
                        <div className="adm-list">
                            {roleVisible.length === 0 ? (
                                <p className="quiet-empty">No flags match.</p>
                            ) : (
                                roleVisible.map((flag) => {
                                    const was = roleOpen.keys.includes(flag.key);
                                    const on = roleDraft.has(flag.key);
                                    return (
                                        <Checkbox
                                            key={flag.id}
                                            label={`${flag.key}${was !== on ? (on ? '  (adding)' : '  (removing)') : ''}`}
                                            checked={on}
                                            disabled={roleSaving}
                                            onChange={() =>
                                                setRoleDraft((cur) => {
                                                    const next = new Set(cur);
                                                    if (next.has(flag.key)) next.delete(flag.key);
                                                    else next.add(flag.key);
                                                    return next;
                                                })
                                            }
                                        />
                                    );
                                })
                            )}
                        </div>
                        {roleError && (
                            <p className="field-e" role="alert" style={{ marginTop: 10 }}>
                                {roleError}
                            </p>
                        )}
                    </>
                )}
            </Dialog>

            <ConfirmDialog
                open={confirmToggle !== null}
                title={
                    confirmToggle
                        ? `Turn ${confirmToggle.value ? 'on' : 'off'} ${FIELD_LABEL[confirmToggle.field]} for ${confirmToggle.row.key}?`
                        : ''
                }
                body={
                    confirmToggle
                        ? `${SCOPE[confirmToggle.field]} It takes effect right away.`
                        : ''
                }
                actionLabel={confirmToggle ? `Turn ${confirmToggle.value ? 'on' : 'off'}` : 'Confirm'}
                danger={confirmToggle ? !confirmToggle.value : false}
                onConfirm={() => confirmToggle && applyToggle(confirmToggle.row, confirmToggle.field, confirmToggle.value)}
                onClose={() => setConfirmToggle(null)}
            />

            <ConfirmDialog
                open={deleting !== null}
                title={deleting ? `Delete ${deleting.key}?` : ''}
                body={`Anything gated on this flag turns off for every user in every org, and it’s removed from all roles${
                    deleting && rolesFor(deleting.key).length > 0 ? ` (${rolesFor(deleting.key).join(', ')})` : ''
                }. This can’t be undone.`}
                actionLabel="Delete flag"
                onConfirm={() => deleting && removeFlag(deleting)}
                onClose={() => setDeleting(null)}
            />
            {toast.node}
        </AreaShell>
    );
}
